const getIds = (items = []) => items.map(({ _id }) => _id).join(',');

const mapById = (items = []) => new Map(items.map((item) => [item._id, item]));

const haveSameIds = (left = [], right = []) => {
  if (left.length !== right.length) {
    return false;
  }

  const leftIds = [...left.map(({ _id }) => _id)].sort();
  const rightIds = [...right.map(({ _id }) => _id)].sort();

  return leftIds.every((id, index) => id === rightIds[index]);
};

const TEMP_ID_PREFIX = 'temp-workbook-v2';
let tempEntityCount = 0;

const mergeEntitiesPreservingDraftOrder = (draftEntities = [], serverEntities = [], mergeExisting) => {
  const serverById = mapById(serverEntities);
  const draftIds = new Set(draftEntities.map(({ _id }) => _id));

  const mergedExisting = draftEntities
    .filter(({ _id }) => serverById.has(_id))
    .map((draftEntity) => mergeExisting(draftEntity, serverById.get(draftEntity._id)));

  const newEntities = serverEntities.filter(({ _id }) => !draftIds.has(_id));

  return [...mergedExisting, ...newEntities];
};

export const cloneWorkbookV2Value = (value) => {
  if (value === null || value === undefined) {
    return value;
  }

  return JSON.parse(JSON.stringify(value));
};

export const createWorkbookV2TempId = (entityType = 'entity') =>
  `${TEMP_ID_PREFIX}-${entityType}-${Date.now()}-${(tempEntityCount += 1)}`;

export const isWorkbookV2TempId = (id) => typeof id === 'string' && id.startsWith(TEMP_ID_PREFIX);

export const createWorkbookV2ChapterDraft = (updates = {}) => {
  const nextChapter = {
    _id: createWorkbookV2TempId('chapter'),
    title: 'New Chapter',
    description: '',
    translations: {},
    lessons: [],
    ...cloneWorkbookV2Value(updates)
  };

  nextChapter.translations = cloneWorkbookV2Value(nextChapter.translations ?? {});
  nextChapter.lessons = cloneWorkbookV2Value(nextChapter.lessons ?? []);

  return nextChapter;
};

export const createWorkbookV2LessonDraft = (updates = {}) => {
  const nextLesson = {
    _id: createWorkbookV2TempId('lesson'),
    title: 'New Lesson',
    translations: {},
    items: [],
    ...cloneWorkbookV2Value(updates)
  };

  nextLesson.translations = cloneWorkbookV2Value(nextLesson.translations ?? {});
  nextLesson.items = cloneWorkbookV2Value(nextLesson.items ?? []);

  return nextLesson;
};

export const createWorkbookV2ItemDraft = (updates = {}) => {
  const nextItem = {
    _id: createWorkbookV2TempId('item'),
    type: 'content',
    text: '',
    translations: {},
    options: {},
    ...cloneWorkbookV2Value(updates)
  };

  nextItem.translations = cloneWorkbookV2Value(nextItem.translations ?? {});
  nextItem.options = cloneWorkbookV2Value(nextItem.options ?? {});

  return nextItem;
};

export const buildWorkbookV2ItemCreatePayload = (item) => {
  const payload = cloneWorkbookV2Value(item);

  delete payload._id;
  delete payload.answer;
  delete payload.answers;
  delete payload.highlights;

  return payload;
};

export const workbookV2EditorReducer = (state, action) => {
  switch (action.type) {
    case 'LOAD_DRAFT': {
      return cloneWorkbookV2Value(action.payload);
    }
    case 'UPDATE_WORKBOOK': {
      return { ...state, ...action.updates };
    }
    case 'SET_COVER_IMAGE': {
      return { ...state, cover_image: action.coverImage };
    }
    case 'SET_CHAPTERS': {
      return { ...state, chapters: cloneWorkbookV2Value(action.chapters) };
    }
    case 'ADD_CHAPTER': {
      return {
        ...state,
        chapters: [...(state.chapters ?? []), cloneWorkbookV2Value(action.chapter)]
      };
    }
    case 'DELETE_CHAPTER': {
      return {
        ...state,
        chapters: (state.chapters ?? []).filter((chapter) => chapter._id !== action.chapterId)
      };
    }
    case 'UPDATE_CHAPTER': {
      return {
        ...state,
        chapters: state.chapters.map((chapter) =>
          chapter._id === action.chapterId ? { ...chapter, ...cloneWorkbookV2Value(action.updates) } : chapter
        )
      };
    }
    case 'SET_CHAPTER_LESSONS': {
      return {
        ...state,
        chapters: state.chapters.map((chapter) =>
          chapter._id === action.chapterId
            ? { ...chapter, lessons: cloneWorkbookV2Value(action.lessons) }
            : chapter
        )
      };
    }
    case 'ADD_LESSON': {
      return {
        ...state,
        chapters: state.chapters.map((chapter) =>
          chapter._id !== action.chapterId
            ? chapter
            : {
                ...chapter,
                lessons: [...(chapter.lessons ?? []), cloneWorkbookV2Value(action.lesson)]
              }
        )
      };
    }
    case 'DELETE_LESSON': {
      return {
        ...state,
        chapters: state.chapters.map((chapter) =>
          chapter._id !== action.chapterId
            ? chapter
            : {
                ...chapter,
                lessons: (chapter.lessons ?? []).filter((lesson) => lesson._id !== action.lessonId)
              }
        )
      };
    }
    case 'UPDATE_LESSON': {
      return {
        ...state,
        chapters: state.chapters.map((chapter) =>
          chapter._id !== action.chapterId
            ? chapter
            : {
                ...chapter,
                lessons: chapter.lessons.map((lesson) =>
                  lesson._id === action.lessonId
                    ? { ...lesson, ...cloneWorkbookV2Value(action.updates) }
                    : lesson
                )
              }
        )
      };
    }
    case 'REPLACE_LESSON': {
      return {
        ...state,
        chapters: state.chapters.map((chapter) =>
          chapter._id !== action.chapterId
            ? chapter
            : {
                ...chapter,
                lessons: chapter.lessons.map((lesson) =>
                  lesson._id === action.lesson._id ? cloneWorkbookV2Value(action.lesson) : lesson
                )
              }
        )
      };
    }
    case 'SET_LESSON_ITEMS': {
      return {
        ...state,
        chapters: state.chapters.map((chapter) =>
          chapter._id !== action.chapterId
            ? chapter
            : {
                ...chapter,
                lessons: chapter.lessons.map((lesson) =>
                  lesson._id === action.lessonId
                    ? { ...lesson, items: cloneWorkbookV2Value(action.items) }
                    : lesson
                )
              }
        )
      };
    }
    case 'ADD_ITEM': {
      return {
        ...state,
        chapters: state.chapters.map((chapter) =>
          chapter._id !== action.chapterId
            ? chapter
            : {
                ...chapter,
                lessons: chapter.lessons.map((lesson) =>
                  lesson._id !== action.lessonId
                    ? lesson
                    : {
                        ...lesson,
                        items: [...(lesson.items ?? []), cloneWorkbookV2Value(action.item)]
                      }
                )
              }
        )
      };
    }
    case 'DELETE_ITEM': {
      return {
        ...state,
        chapters: state.chapters.map((chapter) =>
          chapter._id !== action.chapterId
            ? chapter
            : {
                ...chapter,
                lessons: chapter.lessons.map((lesson) =>
                  lesson._id !== action.lessonId
                    ? lesson
                    : {
                        ...lesson,
                        items: (lesson.items ?? []).filter((item) => item._id !== action.itemId)
                      }
                )
              }
        )
      };
    }
    case 'UPDATE_ITEM': {
      return {
        ...state,
        chapters: state.chapters.map((chapter) =>
          chapter._id !== action.chapterId
            ? chapter
            : {
                ...chapter,
                lessons: chapter.lessons.map((lesson) =>
                  lesson._id !== action.lessonId
                    ? lesson
                    : {
                        ...lesson,
                        items: lesson.items.map((item) =>
                          item._id === action.item._id ? cloneWorkbookV2Value(action.item) : item
                        )
                      }
                )
              }
        )
      };
    }
    default: {
      return state;
    }
  }
};

export const mergeDraftItemsWithServer = (draftItems = [], serverItems = []) => {
  return mergeEntitiesPreservingDraftOrder(draftItems, serverItems, (draftItem, serverItem) => ({
    ...serverItem,
    ...draftItem,
    options: {
      ...(serverItem.options ?? {}),
      ...(draftItem.options ?? {})
    }
  }));
};

export const mergeDraftLessonsWithServer = (draftLessons = [], serverLessons = []) => {
  return mergeEntitiesPreservingDraftOrder(draftLessons, serverLessons, (draftLesson, serverLesson) => ({
    ...serverLesson,
    ...draftLesson,
    items: mergeDraftItemsWithServer(draftLesson.items ?? [], serverLesson.items ?? [])
  }));
};

export const mergeDraftChaptersWithServer = (draftChapters = [], serverChapters = []) => {
  return mergeEntitiesPreservingDraftOrder(draftChapters, serverChapters, (draftChapter, serverChapter) => ({
    ...serverChapter,
    ...draftChapter,
    lessons: mergeDraftLessonsWithServer(draftChapter.lessons ?? [], serverChapter.lessons ?? [])
  }));
};

export const mergeTranslatedLessonWithDraft = (draftLesson, serverLesson) => {
  const mergedItems = mergeEntitiesPreservingDraftOrder(
    draftLesson?.items ?? [],
    serverLesson?.items ?? [],
    (draftItem, translatedItem) => ({
      ...translatedItem,
      ...draftItem,
      translations: cloneWorkbookV2Value(translatedItem.translations ?? draftItem.translations ?? {}),
      options: {
        ...(translatedItem.options ?? {}),
        ...(draftItem.options ?? {}),
        ...(translatedItem.options?.translations
          ? { translations: cloneWorkbookV2Value(translatedItem.options.translations) }
          : {})
      }
    })
  );

  return {
    ...serverLesson,
    ...draftLesson,
    translations: cloneWorkbookV2Value(serverLesson?.translations ?? draftLesson?.translations ?? {}),
    items: mergedItems
  };
};

export const buildWorkbookV2SavePlan = (persistedWorkbook, draftWorkbook) => {
  if (!persistedWorkbook || !draftWorkbook) {
    return {
      workbook: null,
      chapters: [],
      lessons: [],
      items: [],
      creates: {
        chapters: [],
        lessons: [],
        items: []
      },
      deletes: {
        chapters: [],
        lessons: [],
        items: []
      },
      hasChanges: false
    };
  }

  const chapters = [];
  const lessons = [];
  const items = [];
  const creates = {
    chapters: [],
    lessons: [],
    items: []
  };
  const deletes = {
    chapters: [],
    lessons: [],
    items: []
  };

  const persistedChaptersById = mapById(persistedWorkbook.chapters ?? []);
  const persistedLessonsById = new Map();
  const persistedItemsById = new Map();
  const draftChaptersById = mapById(draftWorkbook.chapters ?? []);

  (persistedWorkbook.chapters ?? []).forEach((chapter) => {
    const draftChapter = draftChaptersById.get(chapter._id);

    if (!draftChapter) {
      deletes.chapters.push({ chapterId: chapter._id });
      return;
    }

    const draftLessonsById = mapById(draftChapter.lessons ?? []);

    (chapter.lessons ?? []).forEach((lesson) => {
      persistedLessonsById.set(lesson._id, { chapterId: chapter._id, lesson });

      const draftLesson = draftLessonsById.get(lesson._id);

      if (!draftLesson) {
        deletes.lessons.push({ chapterId: chapter._id, lessonId: lesson._id });
        return;
      }

      const draftItemsById = mapById(draftLesson.items ?? []);

      (lesson.items ?? []).forEach((item) => {
        persistedItemsById.set(item._id, {
          chapterId: chapter._id,
          lessonId: lesson._id,
          item
        });

        if (!draftItemsById.has(item._id)) {
          deletes.items.push({
            chapterId: chapter._id,
            lessonId: lesson._id,
            itemId: item._id
          });
        }
      });
    });
  });

  const chapterIdsChanged = getIds(draftWorkbook.chapters) !== getIds(persistedWorkbook.chapters);
  const chapterOrderChanged = chapterIdsChanged && haveSameIds(draftWorkbook.chapters ?? [], persistedWorkbook.chapters ?? []);

  const workbookChanged =
    draftWorkbook.title !== persistedWorkbook.title ||
    draftWorkbook.description !== persistedWorkbook.description ||
    draftWorkbook.sku !== persistedWorkbook.sku ||
    chapterOrderChanged ||
    JSON.stringify(draftWorkbook.translations ?? {}) !== JSON.stringify(persistedWorkbook.translations ?? {});

  const workbook = workbookChanged
    ? {
        title: draftWorkbook.title,
        description: draftWorkbook.description,
        sku: draftWorkbook.sku,
        translations: cloneWorkbookV2Value(draftWorkbook.translations ?? {})
      }
    : null;

  if (workbook && chapterOrderChanged) {
    workbook.chapters = cloneWorkbookV2Value(draftWorkbook.chapters ?? []);
  }

  (draftWorkbook.chapters ?? []).forEach((chapter) => {
    const persistedChapter = persistedChaptersById.get(chapter._id);

    if (!persistedChapter) {
      creates.chapters.push(cloneWorkbookV2Value(chapter));
      return;
    }

    const lessonIdsChanged = getIds(chapter.lessons) !== getIds(persistedChapter.lessons);
    const lessonOrderChanged = lessonIdsChanged && haveSameIds(chapter.lessons ?? [], persistedChapter.lessons ?? []);
    const chapterChanged =
      chapter.title !== persistedChapter.title ||
      chapter.description !== persistedChapter.description ||
      lessonOrderChanged ||
      JSON.stringify(chapter.translations ?? {}) !== JSON.stringify(persistedChapter.translations ?? {});

    if (chapterChanged) {
      const payload = {
        title: chapter.title,
        description: chapter.description,
        translations: cloneWorkbookV2Value(chapter.translations ?? {})
      };

      if (lessonOrderChanged) {
        payload.lessons = cloneWorkbookV2Value(chapter.lessons ?? []);
      }

      chapters.push({
        chapterId: chapter._id,
        data: payload
      });
    }

    (chapter.lessons ?? []).forEach((lesson) => {
      const persistedLessonEntry = persistedLessonsById.get(lesson._id);

      if (!persistedLessonEntry) {
        creates.lessons.push({
          chapterId: chapter._id,
          lesson: cloneWorkbookV2Value(lesson)
        });
        return;
      }

      const itemIdsChanged = getIds(lesson.items) !== getIds(persistedLessonEntry.lesson.items);
      const itemOrderChanged =
        itemIdsChanged && haveSameIds(lesson.items ?? [], persistedLessonEntry.lesson.items ?? []);
      const lessonChanged =
        lesson.title !== persistedLessonEntry.lesson.title ||
        itemOrderChanged ||
        JSON.stringify(lesson.translations ?? {}) !== JSON.stringify(persistedLessonEntry.lesson.translations ?? {});

      if (lessonChanged) {
        const payload = {
          title: lesson.title,
          translations: cloneWorkbookV2Value(lesson.translations ?? {})
        };

        if (itemOrderChanged) {
          payload.items = cloneWorkbookV2Value(lesson.items ?? []);
        }

        lessons.push({
          chapterId: chapter._id,
          lessonId: lesson._id,
          data: payload
        });
      }

      (lesson.items ?? []).forEach((item) => {
        const persistedItemEntry = persistedItemsById.get(item._id);

        if (!persistedItemEntry) {
          creates.items.push({
            chapterId: chapter._id,
            lessonId: lesson._id,
            item: cloneWorkbookV2Value(item)
          });
          return;
        }

        if (JSON.stringify(item) !== JSON.stringify(persistedItemEntry.item)) {
          items.push({
            chapterId: chapter._id,
            lessonId: lesson._id,
            itemId: item._id,
            data: cloneWorkbookV2Value(item)
          });
        }
      });
    });
  });

  return {
    workbook,
    chapters,
    lessons,
    items,
    creates,
    deletes,
    hasChanges:
      !!workbook ||
      chapters.length > 0 ||
      lessons.length > 0 ||
      items.length > 0 ||
      creates.chapters.length > 0 ||
      creates.lessons.length > 0 ||
      creates.items.length > 0 ||
      deletes.chapters.length > 0 ||
      deletes.lessons.length > 0 ||
      deletes.items.length > 0
  };
};
